import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import { API_BASE_URL } from '../config';
import { useDropzone } from 'react-dropzone';
import {
    Box, Button, Container, Typography,
    alpha, Stack, Chip, createTheme, ThemeProvider, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, FormControlLabel,
    CircularProgress, MenuItem, Grid, Switch, Paper,
    Snackbar, Alert, Tabs, Tab,
    CssBaseline, Divider, Tooltip, Avatar, GlobalStyles,
    Collapse, LinearProgress,
} from '@mui/material';
import {
    ArrowForward, Search, Add, CloudUpload, Edit, Delete, RemoveCircleOutline,
    ImageNotSupported,
    BusinessCenter, MapOutlined, Insights, StairsOutlined, AccountTreeOutlined,
    RouteOutlined, DashboardOutlined, GppGoodOutlined, FactCheckOutlined, EditNoteOutlined,
    HubOutlined, CallMadeOutlined, GridViewOutlined, Construction, Public,
    SecurityOutlined, PolicyOutlined, ListAltOutlined, EventRepeatOutlined, CrisisAlertOutlined,
    SyncOutlined, SummarizeOutlined, DescriptionOutlined, Autorenew, DatasetOutlined,
    ManageSearchOutlined, EngineeringOutlined, FilterAltOutlined, Campaign,
    Movie, Close, 
    DesktopWindows, Apple, PlayArrow, Category, ChevronLeft, ChevronRight,
    Layers, AspectRatio, LockOpen, Lock,
    ExpandMore, ExpandLess, Download, PictureAsPdf, Slideshow, InsertDriveFile,
    PlayCircleOutline, Videocam, OpenInNew, LinkOff, AccessTime,
} from '@mui/icons-material';

import gpcImage from '../assets/gpcimage.jpg';
import bscImage from '../assets/bscimage.jpg';
import grcImage from '../assets/grcimage.jpg';
import pcgImage from '../assets/pcgImage.jpg';
import fgeImage from '../assets/fgeImage.jpg';
import fgqImage from '../assets/fgqImage.jpg';
import desmoImage from '../assets/desmoImage.jpg';
import businesImage from '../assets/businesImage.jpg';
import deimpImage from '../assets/deimpImage.jpg';
import valueImage from '../assets/valueImage.jpg';
import amcImage from '../assets/amcImage.jpg';
import acImage from '../assets/acImage.jpg';
import adcImage from '../assets/adcImage.jpg';
import arvImage from '../assets/arvImage.jpg';
import pgqImage from '../assets/pgqImage.jpg';
import mktImage from '../assets/mktImage.jpg';

// ============================================================================
// TEMA
// ============================================================================
const customTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a', contrastText: '#ffffff' },
        secondary: { main: '#90caf9' },
        background: { default: '#0f1218', paper: '#15181f' },
        divider: 'rgba(255,255,255,0.06)',
    },
    typography: {
        button: { textTransform: 'none', fontWeight: 600 },
        h4: { fontWeight: 800, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
    },
    shape: { borderRadius: 12 },
});

const iconMap = {
    BusinessCenter: <BusinessCenter />, MapOutlined: <MapOutlined />, Insights: <Insights />,
    StairsOutlined: <StairsOutlined />, AccountTreeOutlined: <AccountTreeOutlined />,
    RouteOutlined: <RouteOutlined />, DashboardOutlined: <DashboardOutlined />,
    GppGoodOutlined: <GppGoodOutlined />, FactCheckOutlined: <FactCheckOutlined />,
    EditNoteOutlined: <EditNoteOutlined />, HubOutlined: <HubOutlined />, CallMadeOutlined: <CallMadeOutlined />,
    GridViewOutlined: <GridViewOutlined />, Construction: <Construction />, Public: <Public />,
    SecurityOutlined: <SecurityOutlined />, PolicyOutlined: <PolicyOutlined />, ListAltOutlined: <ListAltOutlined />,
    EventRepeatOutlined: <EventRepeatOutlined />, CrisisAlertOutlined: <CrisisAlertOutlined />,
    SyncOutlined: <SyncOutlined />, SummarizeOutlined: <SummarizeOutlined />,
    DescriptionOutlined: <DescriptionOutlined />, Autorenew: <Autorenew />, DatasetOutlined: <DatasetOutlined />,
    ManageSearchOutlined: <ManageSearchOutlined />, EngineeringOutlined: <EngineeringOutlined />,
    FilterAltOutlined: <FilterAltOutlined />, Campaign: <Campaign />,
};

const localAssets = {
    'gpcimage.jpg': gpcImage, 'bscimage.jpg': bscImage, 'grcimage.jpg': grcImage,
    'pcgImage.jpg': pcgImage, 'fgeImage.jpg': fgeImage, 'fgqImage.jpg': fgqImage,
    'desmoImage.jpg': desmoImage, 'businesImage.jpg': businesImage, 'deimpImage.jpg': deimpImage,
    'valueImage.jpg': valueImage, 'amcImage.jpg': amcImage, 'acImage.jpg': acImage,
    'adcImage.jpg': adcImage, 'arvImage.jpg': arvImage, 'pgqImage.jpg': pgqImage, 'mktImage.jpg': mktImage
};

const statusConfig = {
    online: { label: 'Online', color: 'success', dot: '#4caf50' },
    maintenance: { label: 'Manutenção', color: 'warning', dot: '#ff9800' },
    development: { label: 'Em dev', color: 'info', dot: '#29b6f6' },
    unavailable: { label: 'Offline', color: 'error', dot: '#f44336' },
};

const getAssetUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('blob:') || path.startsWith('http')) return path;
    if (localAssets[path]) return localAssets[path];
    let baseUrl = API_BASE_URL;
    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (isLocalhost) baseUrl = `${window.location.protocol}//${window.location.hostname}:5001`;
    else if (!baseUrl || baseUrl.startsWith('/')) baseUrl = window.location.origin;
    if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
    if (baseUrl.endsWith('/api')) baseUrl = baseUrl.slice(0, -4);
    return `${baseUrl}/uploads/${path}`;
};

// ============================================================================
// HELPER VIMEO
// ============================================================================
function getVimeoEmbedUrl(rawUrl, { autoplay = false, loop = false, muted = false, controls = true, background = false } = {}) {
    if (!rawUrl) return null;
    try {
        const url = String(rawUrl).trim();
        let id = null, hash = null;

        const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/);
        if (playerMatch) {
            id = playerMatch[1];
            const hMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/);
            if (hMatch) hash = hMatch[1];
        } else {
            const vimeoMatch = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
            if (vimeoMatch) {
                id = vimeoMatch[1];
                hash = vimeoMatch[2] || null;
            } else if (/^\d+$/.test(url)) {
                id = url;
            }
        }
        if (!id) return null;

        const params = new URLSearchParams();
        if (autoplay) params.set('autoplay', '1');
        if (muted) params.set('muted', '1');
        if (loop) params.set('loop', '1');
        if (background) params.set('background', '1');
        params.set('controls', controls ? '1' : '0');
        params.set('title', '0');
        params.set('byline', '0');
        params.set('portrait', '0');
        params.set('dnt', '1');
        if (hash) params.set('h', hash);

        return `https://player.vimeo.com/video/${id}?${params.toString()}`;
    } catch (e) { return null; }
}

// ============================================================================
// ÍCONE POR TIPO DE MATERIAL
// ============================================================================
const getMaterialIcon = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'pdf') return <PictureAsPdf sx={{ fontSize: 18, color: '#f44336' }} />;
    if (t === 'pptx' || t === 'ppt') return <Slideshow sx={{ fontSize: 18, color: '#ff9800' }} />;
    if (t === 'docx' || t === 'doc') return <DescriptionOutlined sx={{ fontSize: 18, color: '#2196f3' }} />;
    if (t === 'xlsx' || t === 'xls') return <DescriptionOutlined sx={{ fontSize: 18, color: '#4caf50' }} />;
    if (t === 'zip') return <InsertDriveFile sx={{ fontSize: 18, color: '#9c27b0' }} />;
    if (t === 'link') return <OpenInNew sx={{ fontSize: 18, color: '#90caf9' }} />;
    return <InsertDriveFile sx={{ fontSize: 18, color: '#90caf9' }} />;
};

// ============================================================================
// VÍDEO VIMEO — player inline (com controles nativos do Vimeo)
// ============================================================================
const VimeoThumbnail = ({ url, height = 160 }) => {
    const embedUrl = getVimeoEmbedUrl(url, {
        autoplay: false,    // ⬅️ NÃO inicia sozinho
        loop: false,
        muted: false,       // ⬅️ Com áudio, controles ativos
        controls: true,     // ⬅️ Controles nativos do Vimeo (play/pause/volume/fullscreen)
        background: false,
    });

    if (!embedUrl) {
        return (
            <Box sx={{
                height, bgcolor: '#0f1218', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'text.disabled',
            }}>
                <Movie sx={{ fontSize: 40, opacity: 0.25 }} />
            </Box>
        );
    }

    return (
        <Box sx={{
            position: 'relative',
            height,
            bgcolor: '#000',
            overflow: 'hidden',
            '& iframe': { width: '100%', height: '100%', border: 0 },
        }}>
            <iframe
                src={embedUrl}
                title="Vídeo"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
            />
        </Box>
    );
};
// ============================================================================
// CARD DE CATEGORIA
// ============================================================================
const CategoryCard = ({ category, onClick, isEditing, onEdit, onDelete, isUnlocked }) => {
    const imageUrl = getAssetUrl(category.imageUrl);
    const [imgError, setImgError] = useState(false);
    useEffect(() => setImgError(false), [category.imageUrl]);

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative', width: 360, height: 460, flexShrink: 0,
                borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                bgcolor: '#1a1d24', border: '1px solid rgba(255,255,255,0.06)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: alpha('#90caf9', 0.5),
                    boxShadow: `0 16px 40px ${alpha('#000', 0.7)}`,
                },
            }}
        >
            {!imgError && imageUrl ? (
                <Box component="img" src={imageUrl} alt={category.title}
                    onError={() => setImgError(true)}
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'text.disabled',
                    background: 'linear-gradient(135deg, #15181f 0%, #0f1218 100%)',
                }}>
                    <ImageNotSupported sx={{ fontSize: 64, opacity: 0.25 }} />
                </Box>
            )}

            <Tooltip title={isUnlocked ? 'Você tem acesso' : 'Sem acesso'}>
                <Box sx={{
                    position: 'absolute', top: 16, left: 16,
                    width: 36, height: 36, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isUnlocked ? 'rgba(76, 175, 80, 0.85)' : 'rgba(244, 67, 54, 0.85)',
                    color: 'white', backdropFilter: 'blur(8px)',
                    border: isUnlocked ? '1px solid rgba(76, 175, 80, 0.9)' : '1px solid rgba(244, 67, 54, 0.9)',
                    zIndex: 3,
                }}>
                    {isUnlocked ? <LockOpen sx={{ fontSize: 18 }} /> : <Lock sx={{ fontSize: 18 }} />}
                </Box>
            </Tooltip>

            {category.chip && (
                <Chip label={category.chip} size="small" sx={{
                    position: 'absolute', top: 16, right: 16,
                    height: 24, fontSize: 11, fontWeight: 700,
                    letterSpacing: 0.6, textTransform: 'uppercase',
                    bgcolor: 'rgba(0,0,0,0.7)', color: 'white',
                    border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)',
                }} />
            )}

            {isEditing && (
                <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', bottom: 12, left: 12, zIndex: 2 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(category); }}
                        sx={{ bgcolor: 'rgba(0,0,0,0.75)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.95)' }, width: 34, height: 34 }}>
                        <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(category); }}
                        sx={{ bgcolor: 'rgba(0,0,0,0.75)', color: '#f44336', '&:hover': { bgcolor: 'rgba(0,0,0,0.95)' }, width: 34, height: 34 }}>
                        <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                </Stack>
            )}
        </Box>
    );
};

// ============================================================================
// FILEIRA DE CATEGORIAS
// ============================================================================
const CategoryRow = ({
    categories, systems, userAllowedIds, isSuperAdmin, editMode,
    onSelectCategory, onEditCategory, onDeleteCategory, onNewCategory,
}) => {
    const containerRef = useRef(null);
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(3);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const compute = () => {
            const width = el.clientWidth;
            setItemsPerPage(Math.max(1, Math.floor((width + 20) / (360 + 20))));
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const totalPages = Math.max(1, Math.ceil(categories.length / itemsPerPage));
    useEffect(() => { if (page >= totalPages) setPage(Math.max(0, totalPages - 1)); }, [page, totalPages]);

    const visibleCategories = useMemo(() => {
        const start = page * itemsPerPage;
        return categories.slice(start, start + itemsPerPage);
    }, [categories, page, itemsPerPage]);

    const isCategoryUnlocked = useCallback((category) => {
        if (isSuperAdmin) return true;
        const systemsInCategory = systems.filter(s => (s.section || 'Outros') === category.title);
        if (systemsInCategory.length === 0) return false;
        return systemsInCategory.some(s => userAllowedIds.includes(s.id));
    }, [systems, userAllowedIds, isSuperAdmin]);

    const showNav = totalPages > 1;

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5, minHeight: 40 }}>
                <Typography variant="h6" fontWeight={700}>Categorias</Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {showNav && (
                        <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 0.5 }}>
                            <IconButton size="small" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                                sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { color: 'secondary.main', bgcolor: alpha('#90caf9', 0.1) }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' } }}>
                                <ChevronLeft fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                                sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { color: 'secondary.main', bgcolor: alpha('#90caf9', 0.1) }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' } }}>
                                <ChevronRight fontSize="small" />
                            </IconButton>
                        </Stack>
                    )}
                    {isSuperAdmin && editMode && (
                        <Button variant="outlined" size="small" startIcon={<Add />} onClick={onNewCategory}
                            sx={{ borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.15)', height: 40 }}>
                            Nova categoria
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', width: '100%' }}>
                {visibleCategories.map((category) => (
                    <CategoryCard
                        key={category.id}
                        category={category}
                        isUnlocked={isCategoryUnlocked(category)}
                        onClick={() => onSelectCategory(category)}
                        isEditing={isSuperAdmin && editMode}
                        onEdit={onEditCategory}
                        onDelete={onDeleteCategory}
                    />
                ))}
            </Box>

            {showNav && (
                <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mt: 3 }}>
                    {Array.from({ length: totalPages }).map((_, i) => (
                        <Box key={i} onClick={() => setPage(i)}
                            sx={{
                                width: i === page ? 24 : 8, height: 8, borderRadius: 4,
                                bgcolor: i === page ? 'secondary.main' : 'rgba(255,255,255,0.15)',
                                cursor: 'pointer', transition: 'all 0.25s',
                                '&:hover': { bgcolor: i === page ? 'secondary.main' : 'rgba(255,255,255,0.35)' },
                            }} />
                    ))}
                </Stack>
            )}
        </Box>
    );
};
// ============================================================================
// MODAL DE VÍDEO — layout melhorado com header destacado
// ============================================================================
const VideoPlayerModal = ({ open, onClose, video, software, isAllowed, isSuperAdmin, onAccess }) => {
    if (!video) return null;
    const embedUrl = getVimeoEmbedUrl(video.vimeoUrl, { autoplay: true, controls: true });
    const materials = Array.isArray(software?.materials) ? software.materials : [];

    const isButtonDisabled = !isSuperAdmin && (!isAllowed || software?.status !== 'online');
    const canAccess = isAllowed || isSuperAdmin;
    const hasDownloads = software?.downloadWinUrl || software?.downloadMacUrl;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            disableEnforceFocus
            disableAutoFocus
            style={{ zIndex: 1500 }}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    bgcolor: '#0f1218',
                    backgroundImage: 'none',
                    height: { xs: '100vh', md: '90vh' },
                    maxHeight: { xs: '100vh', md: '90vh' },
                    m: { xs: 0, md: 2 },
                    boxShadow: '0 24px 64px rgba(0,0,0,0.8)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                },
            }}
            BackdropProps={{
                sx: { backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' },
            }}
        >
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '&.MuiDialogContent-root': { pt: 0, pb: 0 },
                }}
            >
                {/* ============ HEADER DO PLAYER ============ */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        bgcolor: 'rgba(255,255,255,0.01)',
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{
                        width: 32, height: 32, borderRadius: 1,
                        bgcolor: 'rgba(144, 202, 249, 0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <PlayCircleOutline sx={{ fontSize: 18, color: 'secondary.main' }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            noWrap
                            sx={{ fontSize: '0.95rem', lineHeight: 1.2 }}
                        >
                            {video.title || 'Vídeo'}
                        </Typography>
                        {software && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ display: 'block', fontSize: '0.72rem' }}
                            >
                                {software.name}
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            width: 32, height: 32,
                            transition: 'all 0.15s',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.1)',
                                color: 'white',
                            },
                        }}
                    >
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* ============ PLAYER ============ */}
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    maxHeight: { xs: '40vh', md: '55vh' },
                    aspectRatio: '16/9',
                    bgcolor: '#000',
                    flexShrink: 0,
                }}>
                    {embedUrl ? (
                        <iframe
                            src={embedUrl}
                            title={video.title || 'Vídeo'}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                        />
                    ) : (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.disabled' }}>
                            <LinkOff sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="body2">URL do vídeo inválida</Typography>
                        </Stack>
                    )}
                </Box>

                {/* ============ INFO + MATERIAIS + BOTÕES ============ */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        p: { xs: 2, md: 2.5 },
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'transparent',
                            borderRadius: 3,
                            transition: 'background 0.2s',
                        },
                        '&:hover::-webkit-scrollbar-thumb': {
                            bgcolor: 'rgba(255,255,255,0.1)',
                        },
                    }}
                >
                    {/* Descrição */}
                    {video.description && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mb: 2,
                                lineHeight: 1.55,
                                whiteSpace: 'pre-line',
                                fontSize: '0.85rem',
                            }}
                        >
                            {video.description}
                        </Typography>
                    )}

                    {/* Layout horizontal: materiais à esquerda, ações à direita */}
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                        {/* MATERIAIS */}
                        {materials.length > 0 && (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.disabled',
                                        fontWeight: 700,
                                        letterSpacing: 0.8,
                                        display: 'block',
                                        mb: 0.75,
                                        fontSize: '0.68rem',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    📎 Materiais ({materials.length})
                                </Typography>
                                <Stack spacing={0.75}>
                                    {materials.map((mat, idx) => (
                                        <Paper
                                            key={idx}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1,
                                                bgcolor: 'rgba(255,255,255,0.02)',
                                                border: '1px solid rgba(255,255,255,0.06)',
                                                borderRadius: 1.5,
                                                transition: 'all 0.15s',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255,255,255,0.04)',
                                                    borderColor: 'rgba(255,255,255,0.12)',
                                                },
                                            }}
                                        >
                                            {getMaterialIcon(mat.type)}
                                            <Typography
                                                variant="body2"
                                                sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
                                                noWrap
                                            >
                                                {mat.name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={mat.url.startsWith('http') ? mat.url : getAssetUrl(mat.url)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: 'secondary.main', p: 0.5 }}
                                            >
                                                <Download sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* AÇÕES */}
                        <Box sx={{
                            width: { xs: '100%', md: materials.length > 0 ? 260 : '100%' },
                            flexShrink: 0,
                        }}>
                            {software?.targetUrl && (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="medium"
                                    endIcon={<ArrowForward fontSize="small" />}
                                    disabled={isButtonDisabled}
                                    onClick={() => onAccess(software)}
                                    sx={{
                                        py: 1.1,
                                        borderRadius: 2,
                                        fontSize: '0.85rem',
                                        mb: hasDownloads ? 1 : 0,
                                    }}
                                >
                                    {canAccess ? 'Acessar Sistema' : 'Não habilitado'}
                                </Button>
                            )}

                            {hasDownloads && (
                                <Stack direction="row" spacing={1}>
                                    {software.downloadWinUrl && (
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DesktopWindows fontSize="small" />}
                                            href={software.downloadWinUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.1)',
                                                fontSize: '0.75rem',
                                                py: 0.75,
                                            }}
                                        >
                                            Windows
                                        </Button>
                                    )}
                                    {software.downloadMacUrl && (
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Apple fontSize="small" />}
                                            href={software.downloadMacUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.1)',
                                                fontSize: '0.75rem',
                                                py: 0.75,
                                            }}
                                        >
                                            Mac
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
// ============================================================================
// SOFTWARE ACCORDION — clique no vídeo abre o player
// ============================================================================
const SoftwareAccordion = ({ system, isAllowed, isSuperAdmin, onAccess, onPlayVideo }) => {
    const [expanded, setExpanded] = useState(false);
    const [playingVideo, setPlayingVideo] = useState(null);

    const videos = Array.isArray(system.videos) ? system.videos : [];
    const materials = Array.isArray(system.materials) ? system.materials : [];

    const isButtonDisabled = !isSuperAdmin && (!isAllowed || system.status !== 'online');

    const items = [
        ...videos.map((v, i) => ({ ...v, _type: 'video', _idx: i })),
        ...materials.map((m, i) => ({ ...m, _type: 'material', _idx: i })),
    ];

    const handlePlayVideo = (video) => {
        setPlayingVideo(video);
    };

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    bgcolor: '#15181f',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: 1,
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.1)' },
                }}
            >
                {/* CABEÇALHO */}
                <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1.25,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                    }}
                >
                    <IconButton size="small" sx={{ color: 'text.secondary', pointerEvents: 'none', p: 0.25 }}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>

                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0, fontSize: '0.9rem' }} noWrap>
                        {system.name}
                    </Typography>

                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                        {items.length} {items.length === 1 ? 'item' : 'itens'}
                    </Typography>
                </Box>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                    <Box sx={{ px: 1, py: 0.5 }}>
                        {items.map((item, idx) => {
                            if (item._type === 'video') {
                                return (
                                    <Box
                                        key={`v-${idx}`}
                                        onClick={() => handlePlayVideo(item)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            transition: 'background 0.15s',
                                            '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.08)' },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 22, height: 22, borderRadius: '50%',
                                            bgcolor: 'rgba(144, 202, 249, 0.15)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <PlayArrow sx={{ fontSize: 14, color: 'secondary.main' }} />
                                        </Box>

                                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: '0.85rem' }} noWrap>
                                            {item.title || `Vídeo ${item._idx + 1}`}
                                        </Typography>

                                        {item.duration && (
                                            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: '0.7rem' }}>
                                                {item.duration}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            }

                            const fileUrl = item.url?.startsWith('http') ? item.url : getAssetUrl(item.url);
                            return (
                                <Box
                                    key={`m-${idx}`}
                                    component="a"
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        transition: 'background 0.15s',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                                    }}
                                >
                                    <Box sx={{
                                        width: 22, height: 22, borderRadius: '50%',
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                        color: 'text.secondary',
                                    }}>
                                        {getMaterialIcon(item.type)}
                                    </Box>

                                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: '0.85rem' }} noWrap>
                                        {item.name}
                                    </Typography>

                                    <Download sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                                </Box>
                            );
                        })}

                        {items.length === 0 && (
                            <Box sx={{ px: 1.5, py: 1.5 }}>
                                <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    Este software ainda não tem vídeos ou materiais.
                                </Typography>
                            </Box>
                        )}

                        {system.targetUrl && (
                            <Box sx={{ px: 1, py: 1.25, pt: 1.5 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    endIcon={<ArrowForward fontSize="small" />}
                                    disabled={isButtonDisabled}
                                    onClick={() => onAccess(system)}
                                    sx={{ borderRadius: 1.5, py: 0.75, fontSize: '0.8rem' }}
                                >
                                    {isAllowed || isSuperAdmin ? 'Acessar Sistema' : 'Não habilitado'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Paper>

            {/* PLAYER — passa isAllowed, isSuperAdmin e onAccess */}
            <VideoPlayerModal
                open={!!playingVideo}
                onClose={() => setPlayingVideo(null)}
                video={playingVideo}
                software={system}
                isAllowed={isAllowed}
                isSuperAdmin={isSuperAdmin}
                onAccess={onAccess}
            />
        </>
    );
};
// ============================================================================
// MODAL DE CATEGORIA — X no canto superior direito
// ============================================================================
const CategoryModal = ({
    open, onClose, category, systems, isAllowedMap, isSuperAdmin, onAccess,
}) => {
    const [playingVideo, setPlayingVideo] = useState(null);
    const [playingSoftware, setPlayingSoftware] = useState(null);
    const [imgError, setImgError] = useState(false);

    useEffect(() => { setImgError(false); }, [category?.imageUrl]);

    if (!category) return null;

    const categoryImageUrl = getAssetUrl(category.imageUrl);

    const totalItems = systems.reduce((acc, sys) => {
        const v = Array.isArray(sys.videos) ? sys.videos.length : 0;
        const m = Array.isArray(sys.materials) ? sys.materials.length : 0;
        return acc + v + m;
    }, 0);

    const handlePlayVideo = (video, software) => {
        setPlayingVideo(video);
        setPlayingSoftware(software);
    };

    const closePlayer = () => {
        setPlayingVideo(null);
        setPlayingSoftware(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, md: 3 },
                        bgcolor: '#0f1218',
                        backgroundImage: 'none',
                        height: { xs: '100vh', md: '90vh' },
                        maxHeight: { xs: '100vh', md: '90vh' },
                        m: { xs: 0, md: 2 },
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        overflow: 'hidden',
                        '&.MuiDialogContent-root': { pt: 0, pb: 0 },
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                    }}>
                        {/* BOTÃO X — CANTO SUPERIOR DIREITO */}
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                zIndex: 10,
                                color: 'white',
                                bgcolor: 'rgba(0,0,0,0.6)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                backdropFilter: 'blur(8px)',
                                width: 32, height: 32,
                                transition: 'all 0.15s',
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.85)',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <Close sx={{ fontSize: 18 }} />
                        </IconButton>

                        {/* ============ COLUNA ESQUERDA ============ */}
                        <Box sx={{
                            width: { xs: '100%', md: '32%' },
                            p: { xs: 2, md: 3 },
                            borderRight: { md: '1px solid rgba(255,255,255,0.05)' },
                            borderBottom: { xs: '1px solid rgba(255,255,255,0.05)', md: 'none' },
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}>
                            {/* MÍDIA */}
                            <Box sx={{
                                width: '100%',
                                aspectRatio: '16/9',
                                maxHeight: { xs: 140, md: 180 },
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                bgcolor: '#000',
                                mb: 2,
                                position: 'relative',
                                flexShrink: 0,
                            }}>
                                {category.vimeoUrl ? (
                                    <VimeoThumbnail
                                        url={category.vimeoUrl}
                                        height="100%"
                                    />
                                ) : !imgError && categoryImageUrl ? (
                                    <Box
                                        component="img"
                                        src={categoryImageUrl}
                                        alt={category.title}
                                        onError={() => setImgError(true)}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.disabled' }}>
                                        <ImageNotSupported sx={{ fontSize: 32, opacity: 0.3 }} />
                                    </Stack>
                                )}
                            </Box>

                            {/* CHIP */}
                            {category.chip && (
                                <Chip
                                    label={category.chip}
                                    size="small"
                                    sx={{
                                        alignSelf: 'flex-start',
                                        mb: 1,
                                        height: 20, fontSize: 9, fontWeight: 700,
                                        letterSpacing: 0.5, textTransform: 'uppercase',
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                        color: 'secondary.main',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                    }}
                                />
                            )}

                            {/* TÍTULO */}
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{
                                    mb: 1,
                                    lineHeight: 1.25,
                                    fontSize: { xs: '1.05rem', md: '1.1rem' },
                                }}
                            >
                                {category.title}
                            </Typography>

                            {/* DESCRIÇÃO */}
                            {category.description && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mb: 1.5,
                                        lineHeight: 1.5,
                                        whiteSpace: 'pre-line',
                                        fontSize: '0.82rem',
                                        flex: 1,
                                        minHeight: 0,
                                        overflowY: 'auto',
                                        pr: 0.5,
                                        '&::-webkit-scrollbar': { width: 4 },
                                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
                                    }}
                                >
                                    {category.description}
                                </Typography>
                            )}

                            {/* STATS */}
                            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 0.75, mt: 'auto' }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Category sx={{ fontSize: 13, color: 'text.disabled' }} />
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                        {systems.length} {systems.length === 1 ? 'módulo' : 'módulos'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Layers sx={{ fontSize: 13, color: 'text.disabled' }} />
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                        {totalItems} {totalItems === 1 ? 'conteúdo' : 'conteúdos'}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        {/* ============ COLUNA DIREITA ============ */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 0,
                                p: { xs: 2, md: 3 },
                                pt: { xs: 2, md: 3 },
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: 'transparent',
                                    borderRadius: 3,
                                    transition: 'background 0.2s',
                                },
                                '&:hover::-webkit-scrollbar-thumb': {
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: 'text.disabled',
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    display: 'block',
                                    mb: 1.5,
                                    fontSize: '0.68rem',
                                }}
                            >
                                Módulos desta categoria
                            </Typography>

                            {systems.length === 0 ? (
                                <Stack alignItems="center" justifyContent="center" sx={{ py: 6, color: 'text.disabled' }}>
                                    <Category sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                        Nenhum software nesta categoria.
                                    </Typography>
                                </Stack>
                            ) : (
                                systems.map(sys => (
                                    <SoftwareAccordion
                                        key={sys.id}
                                        system={sys}
                                        isAllowed={isAllowedMap[sys.id]}
                                        isSuperAdmin={isSuperAdmin}
                                        onAccess={onAccess}
                                        onPlayVideo={handlePlayVideo}
                                    />
                                ))
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <VideoPlayerModal
                open={!!playingVideo}
                onClose={closePlayer}
                video={playingVideo}
                software={playingSoftware}
                isAllowed={playingSoftware ? isAllowedMap[playingSoftware.id] : false}
                isSuperAdmin={isSuperAdmin}
                onAccess={onAccess}
            />
        </>
    );
};

// ============================================================================
// MODAL CRIAR/EDITAR CATEGORIA
// ============================================================================
const CategoryFormModal = ({ open, onClose, onSave, category, existingTitles }) => {
    const [formData, setFormData] = useState({ title: '', chip: '', description: '', imageUrl: '', vimeoUrl: '' });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setFormData({
                title: category?.title || '',
                chip: category?.chip || '',
                description: category?.description || '',
                imageUrl: category?.imageUrl || '',
                vimeoUrl: category?.vimeoUrl || '',
            });
            setImageFile(null);
            setImagePreview(category?.imageUrl ? getAssetUrl(category.imageUrl) : null);
            setError('');
            setSaving(false);
        }
    }, [open, category]);

    const onDrop = useCallback((files) => {
        const file = files[0];
        if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop, accept: { 'image/*': [] }, multiple: false,
    });

    const handleSave = async () => {
        if (!formData.title.trim()) { setError('O nome da categoria é obrigatório.'); return; }
        const isDuplicate = existingTitles
            .filter(t => t !== category?.title)
            .some(t => t.toLowerCase() === formData.title.trim().toLowerCase());
        if (isDuplicate) { setError('Já existe uma categoria com esse nome.'); return; }

        setSaving(true);
        try {
            const data = new FormData();
            data.append('title', formData.title.trim());
            data.append('chip', formData.chip || '');
            data.append('description', formData.description || '');
            data.append('vimeoUrl', formData.vimeoUrl || '');
            if (imageFile) data.append('image', imageFile);
            await onSave(data, category);
        } catch (e) {
            setError('Erro ao salvar categoria.');
        } finally {
            setSaving(false);
        }
    };

    const vimeoPreview = formData.vimeoUrl ? getVimeoEmbedUrl(formData.vimeoUrl, { controls: true }) : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700}>{category ? 'Editar categoria' : 'Nova categoria'}</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    <Box {...getRootProps()}>
                        <input {...getInputProps()} />
                        <Box sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'rgba(255,255,255,0.1)',
                            borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                            bgcolor: isDragActive ? alpha('#90caf9', 0.05) : 'rgba(255,255,255,0.02)',
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#90caf9', 0.03) },
                        }}>
                            {imagePreview ? (
                                <Box>
                                    <img src={imagePreview} alt="preview"
                                        style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 8, display: 'block', margin: '0 auto' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        Clique para trocar a imagem
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <CloudUpload sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">Clique ou arraste uma imagem</Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Alert severity="info" icon={<AspectRatio fontSize="small" />}
                        sx={{ bgcolor: alpha('#90caf9', 0.06), border: `1px solid ${alpha('#90caf9', 0.15)}`, '& .MuiAlert-icon': { color: 'secondary.main' } }}>
                        <Typography variant="caption">
                            • <strong>Imagem:</strong> 1080 × 1380 px (retrato)<br/>
                            • <strong>Vídeo:</strong> qualquer link do Vimeo
                        </Typography>
                    </Alert>

                    <TextField label="Nome da categoria" fullWidth required
                        value={formData.title}
                        onChange={(e) => { setFormData({ ...formData, title: e.target.value }); setError(''); }}
                        error={!!error} helperText={error} />

                    <TextField label="Chip (opcional)" fullWidth
                        value={formData.chip}
                        onChange={(e) => setFormData({ ...formData, chip: e.target.value })}
                        placeholder="Ex: Essencial, Novo, Em breve"
                        inputProps={{ maxLength: 20 }} />

                    <TextField label="Descrição" fullWidth multiline rows={4}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Descrição longa que aparece na coluna esquerda do modal" />

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                        <Chip label="VÍDEO DE ABERTURA" size="small" sx={{ fontSize: 10, fontWeight: 700 }} />
                    </Divider>

                    <TextField label="URL do vídeo Vimeo (opcional)" fullWidth
                        value={formData.vimeoUrl}
                        onChange={(e) => setFormData({ ...formData, vimeoUrl: e.target.value })}
                        placeholder="https://vimeo.com/123456789"
                        InputProps={{ startAdornment: <InputAdornment position="start"><Movie sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                        helperText="Aparece no topo da coluna esquerda do modal." />

                    {vimeoPreview && (
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', aspectRatio: '16/9', bgcolor: '#000' }}>
                            <iframe src={vimeoPreview} title="Preview" allowFullScreen
                                style={{ width: '100%', height: '100%', border: 0 }} />
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============================================================================
// MODAL CRIAR/EDITAR SOFTWARE (com abas)
// ============================================================================
const SoftwareFormModal = ({ open, onClose, onSave, system, categories }) => {
    const [formData, setFormData] = useState({
        name: '', subtitle: '', description: '', targetUrl: '', eadUrl: '',
        downloadWinUrl: '', downloadMacUrl: '', section: '', status: 'online',
        vimeoThumbnailUrl: '',
    });
    const [videos, setVideos] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [newMaterialUrl, setNewMaterialUrl] = useState({ name: '', url: '' });
    const [uploadingMaterial, setUploadingMaterial] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState(0);

    useEffect(() => {
        if (open) {
            if (system) {
                setFormData({
                    name: system.name || '',
                    subtitle: system.subtitle || '',
                    description: system.description || '',
                    targetUrl: system.targetUrl || '',
                    eadUrl: system.eadUrl || '',
                    downloadWinUrl: system.downloadWinUrl || '',
                    downloadMacUrl: system.downloadMacUrl || '',
                    section: system.section || (categories[0]?.title || ''),
                    status: system.status || 'online',
                    vimeoThumbnailUrl: system.vimeoThumbnailUrl || '',
                });
                setVideos(Array.isArray(system.videos) ? system.videos : []);
                setMaterials(Array.isArray(system.materials) ? system.materials : []);
                setImagePreview(getAssetUrl(system.imageUrl));
            } else {
                setFormData({
                    name: '', subtitle: '', description: '', targetUrl: '', eadUrl: '',
                    downloadWinUrl: '', downloadMacUrl: '',
                    section: categories[0]?.title || '', status: 'online',
                    vimeoThumbnailUrl: '',
                });
                setVideos([]);
                setMaterials([]);
                setImagePreview(null);
            }
            setImageFile(null);
            setSaving(false);
            setTab(0);
        }
    }, [open, system, categories]);

    const onDrop = useCallback((files) => {
        const file = files[0];
        if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
    }, []);
    const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { 'image/*': [] }, multiple: false });

    const handleAddVideo = () => setVideos([...videos, { title: '', vimeoUrl: '', description: '', duration: '' }]);
    const handleRemoveVideo = (idx) => setVideos(videos.filter((_, i) => i !== idx));
    const handleUpdateVideo = (idx, field, value) => {
        const updated = [...videos];
        updated[idx] = { ...updated[idx], [field]: value };
        setVideos(updated);
    };

    const handleUploadMaterial = async (files) => {
        const file = files[0];
        if (!file) return;
        if (!system?.id) { alert('Salve o software primeiro.'); return; }
        setUploadingMaterial(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await api.post(`/softwares/${system.id}/materials`, fd, { headers: { 'Content-Type': undefined } });
            setMaterials(res.data.materials || []);
        } catch (err) {
            alert('Erro: ' + (err.response?.data?.message || err.message));
        } finally { setUploadingMaterial(false); }
    };

    const handleAddLinkMaterial = async () => {
        if (!newMaterialUrl.name || !newMaterialUrl.url) return;
        if (!system?.id) { alert('Salve o software primeiro.'); return; }
        try {
            const res = await api.post(`/softwares/${system.id}/materials/link`, {
                name: newMaterialUrl.name, url: newMaterialUrl.url, type: 'link',
            });
            setMaterials(res.data.materials || []);
            setNewMaterialUrl({ name: '', url: '' });
        } catch (err) { alert('Erro: ' + (err.response?.data?.message || err.message)); }
    };

    const handleRemoveMaterial = async (idx) => {
        if (!system?.id) { setMaterials(materials.filter((_, i) => i !== idx)); return; }
        if (!window.confirm('Remover este material?')) return;
        try {
            const res = await api.delete(`/softwares/${system.id}/materials/${idx}`);
            setMaterials(res.data.materials || []);
        } catch (err) { alert('Erro ao remover.'); }
    };

    const { getRootProps: getMatRootProps, getInputProps: getMatInputProps } = useDropzone({
        onDrop: handleUploadMaterial,
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/zip': ['.zip'],
        },
        multiple: false,
    });

    const handleSave = async () => {
        if (!formData.name.trim()) return;
        setSaving(true);
        try {
            const data = new FormData();
            Object.keys(formData).forEach(k => data.append(k, formData[k] || ''));
            data.append('metadata', JSON.stringify([]));
            data.append('videos', JSON.stringify(videos));
            data.append('materials', JSON.stringify(materials));
            if (imageFile) data.append('image', imageFile);
            await onSave(data, system);
        } finally { setSaving(false); }
    };

    const vimeoPreview = formData.vimeoThumbnailUrl
        ? getVimeoEmbedUrl(formData.vimeoThumbnailUrl, { muted: true, loop: true, autoplay: false, controls: true })
        : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight={700}>{system ? 'Editar card' : 'Novo card'}</DialogTitle>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Tab label="Informações" />
                <Tab label={`Vídeos (${videos.length})`} />
                <Tab label={`Materiais (${materials.length})`} />
            </Tabs>

            <DialogContent dividers sx={{ borderColor: 'transparent', minHeight: 480 }}>
                {tab === 0 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Box {...getRootProps()} sx={{
                            border: '2px dashed', borderColor: 'rgba(255,255,255,0.1)',
                            p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.02)',
                        }}>
                            <input {...getInputProps()} />
                            {imagePreview ? (
                                <img src={imagePreview} alt="" style={{ maxHeight: 150, maxWidth: '100%', borderRadius: 8 }} />
                            ) : (
                                <Typography color="text.secondary">
                                    <CloudUpload sx={{ mr: 1, verticalAlign: 'middle' }} /> Imagem do card
                                </Typography>
                            )}
                        </Box>

                        <TextField label="Nome" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} fullWidth required />
                        <TextField label="Subtítulo" value={formData.subtitle} onChange={e => setFormData({ ...formData, subtitle: e.target.value })} fullWidth />

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField select label="Categoria" value={formData.section} onChange={e => setFormData({ ...formData, section: e.target.value })} fullWidth>
                                    {categories.map(s => <MenuItem key={s.id} value={s.title}>{s.title}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField select label="Status" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} fullWidth>
                                    {Object.keys(statusConfig).map(k => <MenuItem key={k} value={k}>{statusConfig[k].label}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={6}><TextField label="Download Windows" value={formData.downloadWinUrl} onChange={e => setFormData({ ...formData, downloadWinUrl: e.target.value })} fullWidth size="small" /></Grid>
                            <Grid item xs={6}><TextField label="Download Mac" value={formData.downloadMacUrl} onChange={e => setFormData({ ...formData, downloadMacUrl: e.target.value })} fullWidth size="small" /></Grid>
                        </Grid>

                        <TextField label="URL Sistema" value={formData.targetUrl} onChange={e => setFormData({ ...formData, targetUrl: e.target.value })} fullWidth />
                        <TextField label="URL EAD" value={formData.eadUrl} onChange={e => setFormData({ ...formData, eadUrl: e.target.value })} fullWidth />
                        <TextField label="Descrição" multiline rows={4} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} fullWidth />

                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            <Chip label="MINIATURA EM VÍDEO" size="small" sx={{ fontSize: 10, fontWeight: 700 }} />
                        </Divider>

                        <TextField
                            label="URL do vídeo Vimeo (miniatura)"
                            fullWidth
                            value={formData.vimeoThumbnailUrl}
                            onChange={e => setFormData({ ...formData, vimeoThumbnailUrl: e.target.value })}
                            placeholder="https://vimeo.com/123456789"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Movie sx={{ color: 'secondary.main' }} /></InputAdornment> }}
                            helperText="Aparece como miniatura viva no mini-card."
                        />

                        {vimeoPreview && (
                            <Box sx={{ borderRadius: 2, overflow: 'hidden', aspectRatio: '16/9', bgcolor: '#000' }}>
                                <iframe src={vimeoPreview} title="Preview" allowFullScreen
                                    style={{ width: '100%', height: '100%', border: 0 }} />
                            </Box>
                        )}
                    </Stack>
                )}

                {tab === 1 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info" sx={{ bgcolor: alpha('#90caf9', 0.06), border: `1px solid ${alpha('#90caf9', 0.15)}`, '& .MuiAlert-icon': { color: 'secondary.main' } }}>
                            <Typography variant="caption">
                                Adicione os vídeos do módulo. A <strong>duração</strong> aparece ao lado do título (ex: 14:07).
                            </Typography>
                        </Alert>

                        {videos.map((video, idx) => (
                            <Paper key={idx} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" fontWeight={700} color="secondary.main">
                                        VÍDEO {idx + 1}
                                    </Typography>
                                    <IconButton size="small" onClick={() => handleRemoveVideo(idx)} sx={{ color: '#f44336' }}>
                                        <RemoveCircleOutline fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <Stack spacing={1.5}>
                                    <TextField label="Título do vídeo" size="small" fullWidth
                                        value={video.title}
                                        onChange={(e) => handleUpdateVideo(idx, 'title', e.target.value)}
                                        placeholder="Ex: Introdução ao módulo" />
                                    <Stack direction="row" spacing={1.5}>
                                        <TextField label="URL do Vimeo" size="small" fullWidth
                                            value={video.vimeoUrl}
                                            onChange={(e) => handleUpdateVideo(idx, 'vimeoUrl', e.target.value)}
                                            placeholder="https://vimeo.com/123456789"
                                            InputProps={{ startAdornment: <InputAdornment position="start"><Movie sx={{ color: 'secondary.main', fontSize: 18 }} /></InputAdornment> }} />
                                        <TextField label="Duração" size="small" sx={{ width: 120 }}
                                            value={video.duration}
                                            onChange={(e) => handleUpdateVideo(idx, 'duration', e.target.value)}
                                            placeholder="14:07"
                                            InputProps={{ startAdornment: <InputAdornment position="start"><AccessTime sx={{ color: 'text.disabled', fontSize: 16 }} /></InputAdornment> }} />
                                    </Stack>
                                    <TextField label="Descrição (opcional)" size="small" fullWidth multiline rows={2}
                                        value={video.description}
                                        onChange={(e) => handleUpdateVideo(idx, 'description', e.target.value)} />
                                </Stack>
                            </Paper>
                        ))}

                        <Button variant="outlined" startIcon={<Add />} onClick={handleAddVideo}
                            sx={{ borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.15)', py: 1.5 }}>
                            Adicionar vídeo
                        </Button>
                    </Stack>
                )}

                {tab === 2 && (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {!system?.id && (
                            <Alert severity="warning">Salve o software primeiro antes de anexar materiais.</Alert>
                        )}

                        <Alert severity="info" sx={{ bgcolor: alpha('#90caf9', 0.06), border: `1px solid ${alpha('#90caf9', 0.15)}`, '& .MuiAlert-icon': { color: 'secondary.main' } }}>
                            <Typography variant="caption">
                                Aceita PDF, PPTX, DOCX, XLSX e ZIP (até 50 MB). Também aceita links externos.
                            </Typography>
                        </Alert>

                        <Box {...getMatRootProps()} sx={{
                            border: '2px dashed', borderColor: 'rgba(255,255,255,0.1)',
                            p: 3, textAlign: 'center', cursor: system?.id ? 'pointer' : 'not-allowed',
                            borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)',
                            opacity: system?.id ? 1 : 0.5,
                        }}>
                            <input {...getMatInputProps()} />
                            {uploadingMaterial ? (
                                <CircularProgress size={28} />
                            ) : (
                                <>
                                    <CloudUpload sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">Clique ou arraste um arquivo</Typography>
                                </>
                            )}
                        </Box>

                        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                            <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ display: 'block', mb: 1.5 }}>
                                OU ADICIONE UM LINK EXTERNO
                            </Typography>
                            <Stack spacing={1.5}>
                                <TextField label="Nome do material" size="small" fullWidth
                                    value={newMaterialUrl.name}
                                    onChange={(e) => setNewMaterialUrl({ ...newMaterialUrl, name: e.target.value })}
                                    placeholder="Ex: Apresentação no Drive" />
                                <Stack direction="row" spacing={1}>
                                    <TextField label="URL" size="small" fullWidth
                                        value={newMaterialUrl.url}
                                        onChange={(e) => setNewMaterialUrl({ ...newMaterialUrl, url: e.target.value })}
                                        placeholder="https://drive.google.com/..." />
                                    <Button variant="contained" onClick={handleAddLinkMaterial}
                                        disabled={!system?.id || !newMaterialUrl.name || !newMaterialUrl.url}>
                                        Adicionar
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {materials.length > 0 && (
                            <>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                    <Chip label="MATERIAIS ANEXADOS" size="small" sx={{ fontSize: 10, fontWeight: 700 }} />
                                </Divider>
                                <Stack spacing={1}>
                                    {materials.map((mat, idx) => (
                                        <Paper key={idx} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            p: 1.5, bgcolor: 'rgba(255,255,255,0.02)',
                                            border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2,
                                        }}>
                                            {getMaterialIcon(mat.type)}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>{mat.name}</Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                    {mat.type?.toUpperCase()} {mat.size ? `• ${(mat.size / 1024 / 1024).toFixed(1)} MB` : ''}
                                                </Typography>
                                            </Box>
                                            <IconButton size="small" onClick={() => handleRemoveMaterial(idx)} sx={{ color: '#f44336' }}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Paper>
                                    ))}
                                </Stack>
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============================================================================
// CARD DE SOFTWARE (grid "Todos os softwares")
// ============================================================================
const SoftwareCard = ({ system, isAllowed, isSuperAdmin, isEditMode, onAccess, onEdit, onDelete }) => {
    const currentStatus = statusConfig[system.status] || statusConfig.unavailable;
    const [imgError, setImgError] = useState(false);
    useEffect(() => setImgError(false), [system.imageUrl, system.updatedAt]);
    const imageUrl = getAssetUrl(system.imageUrl);

    const isButtonDisabled = !isSuperAdmin && (!isAllowed || system.status !== 'online');

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex', flexDirection: 'column',
                bgcolor: '#15181f', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 3, overflow: 'hidden', minHeight: 340,
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: alpha('#90caf9', 0.4),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha('#000', 0.4)}`,
                },
            }}
        >
            <Box sx={{ position: 'relative', height: 130, bgcolor: '#0f1218' }}>
                {!imgError && imageUrl ? (
                    <img src={imageUrl} alt={system.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setImgError(true)} />
                ) : (
                    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.disabled' }}>
                        <ImageNotSupported fontSize="large" />
                    </Stack>
                )}
                <Chip label={currentStatus.label} size="small"
                    sx={{
                        position: 'absolute', top: 10, left: 10,
                        fontSize: 10, height: 20, fontWeight: 700,
                        bgcolor: 'rgba(0,0,0,0.75)', color: 'white',
                        border: `1px solid ${alpha(currentStatus.dot, 0.6)}`,
                        backdropFilter: 'blur(6px)',
                    }} />
                {isSuperAdmin && isEditMode && (
                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(system); }}
                            sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white', width: 28, height: 28, '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' } }}>
                            <Edit sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(system); }}
                            sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#f44336', width: 28, height: 28, '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' } }}>
                            <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Stack>
                )}
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.5 }}>{system.name}</Typography>
                {system.subtitle && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ mb: 0.75 }}>{system.subtitle}</Typography>
                )}
                <Typography variant="caption" color="text.secondary"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4, flex: 1 }}>
                    {system.description || 'Sem descrição.'}
                </Typography>
                <Button fullWidth variant="contained" size="small" disabled={isButtonDisabled}
                    onClick={() => onAccess(system)}
                    endIcon={<ArrowForward fontSize="small" />}
                    sx={{ mt: 2, py: 0.9, borderRadius: 2 }}>
                    {(isAllowed || isSuperAdmin) ? 'Acessar sistema' : 'Não habilitado'}
                </Button>
            </Box>
        </Paper>
    );
};

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================
const DashboardPage = () => {
    const { user, isAuthenticated } = useAuth();
    const isSuperAdmin = user?.role === 'superadmin';

    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [systems, setSystems] = useState([]);
    const [userAllowedIds, setUserAllowedIds] = useState([]);
    const [dashboardConfig, setDashboardConfig] = useState({ banner: {}, sectionsConfig: [] });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [showOnlyAllowed, setShowOnlyAllowed] = useState(true);

    const [openCategoryModal, setOpenCategoryModal] = useState(null);
    const [openCategoryForm, setOpenCategoryForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);

    const [openSoftwareForm, setOpenSoftwareForm] = useState(false);
    const [editingSoftware, setEditingSoftware] = useState(null);

    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchAllData = useCallback(async (background = false) => {
        if (!background) setLoading(true);
        try {
            let config = { banner: {}, sectionsConfig: [] };
            try { const configRes = await api.get('/dashboard/config'); config = configRes.data || config; } catch (e) {}
            const allSoftwaresRes = await api.get('/softwares');
            let loadedSystems = allSoftwaresRes.data || [];
            let allowedIds = [];
            if (isSuperAdmin) allowedIds = loadedSystems.map(s => s.id);
            else if (user?.id) {
                try {
                    const permissionsRes = await api.get(`/softwares/user/${user.id}`);
                    allowedIds = permissionsRes.data.map(s => s.id);
                } catch (e) {}
            }
            setUserAllowedIds(allowedIds);
            loadedSystems.sort((a, b) => (a.order || 0) - (b.order || 0));

            const existingSections = new Set((config.sectionsConfig || []).map(s => s.title));
            const sectionsFromSoftwares = [...new Set(loadedSystems.map(s => s.section || 'Outros'))];
            let newSectionsConfig = [...(config.sectionsConfig || [])];
            let changed = false;
            sectionsFromSoftwares.forEach((secTitle) => {
                if (!existingSections.has(secTitle)) {
                    newSectionsConfig.push({
                        id: `sec-${Date.now()}-${Math.random()}`,
                        title: secTitle, chip: '', description: '', imageUrl: '', vimeoUrl: '',
                        order: newSectionsConfig.length,
                    });
                    changed = true;
                }
            });
            config.sectionsConfig = newSectionsConfig;
            if (changed && isSuperAdmin) {
                try { await api.put('/dashboard/config', { sectionsConfig: newSectionsConfig }); } catch (e) {}
            }

            setDashboardConfig(config);
            setSystems(loadedSystems);
        } catch (error) {
            console.error("Erro dashboard:", error);
        } finally {
            if (!background) setLoading(false);
        }
    }, [isSuperAdmin, user?.id]);

    useEffect(() => { if (isAuthenticated) fetchAllData(); }, [isAuthenticated, fetchAllData]);

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const handleOpenNewCategory = () => { setEditingCategory(null); setOpenCategoryForm(true); };
    const handleOpenEditCategory = (category) => { setEditingCategory(category); setOpenCategoryForm(true); };

    const handleSaveCategory = async (formData, existingCategory) => {
        try {
            const sections = [...(dashboardConfig.sectionsConfig || [])];

            let uploadedImageUrl = existingCategory?.imageUrl || '';
            const imageFile = formData.get('image');
            if (imageFile) {
                try {
                    const uploadData = new FormData();
                    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                    const ext = imageFile.name?.split('.').pop() || 'jpg';
                    uploadData.append('image', imageFile, `category-${uniqueSuffix}.${ext}`);

                    const uploadRes = await api.post('/dashboard/category-image', uploadData, {
                        headers: { 'Content-Type': undefined },
                    });
                    uploadedImageUrl = uploadRes.data.imageUrl;
                } catch (e) {
                    console.error('Erro no upload:', e);
                }
            }

            const vimeoUrl = formData.get('vimeoUrl') || '';

            if (existingCategory) {
                const idx = sections.findIndex(s => s.id === existingCategory.id);
                if (idx >= 0) {
                    sections[idx] = {
                        ...sections[idx],
                        title: formData.get('title'),
                        chip: formData.get('chip'),
                        description: formData.get('description'),
                        imageUrl: uploadedImageUrl,
                        vimeoUrl,
                    };

                    if (existingCategory.title !== formData.get('title')) {
                        for (const sys of systems.filter(s => s.section === existingCategory.title)) {
                            await api.put(`/softwares/${sys.id}`, { section: formData.get('title') });
                        }
                    }
                }
            } else {
                sections.push({
                    id: `sec-${Date.now()}`,
                    title: formData.get('title'),
                    chip: formData.get('chip'),
                    description: formData.get('description'),
                    imageUrl: uploadedImageUrl,
                    vimeoUrl,
                    order: sections.length,
                });
            }

            setDashboardConfig(prev => ({ ...prev, sectionsConfig: sections }));
            await api.put('/dashboard/config', { sectionsConfig: sections });
            setOpenCategoryForm(false);
            setEditingCategory(null);
            setSnackbar({ open: true, message: existingCategory ? 'Categoria atualizada!' : 'Categoria criada!', severity: 'success' });
            fetchAllData(true);
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, message: 'Erro ao salvar categoria.', severity: 'error' });
            throw e;
        }
    };

    const handleOpenNewSoftware = () => { setEditingSoftware(null); setOpenSoftwareForm(true); };
    const handleOpenEditSoftware = (sys) => { setEditingSoftware(sys); setOpenSoftwareForm(true); };

    const handleSaveSoftware = async (formData, existing) => {
        try {
            const cfg = { headers: { 'Content-Type': undefined } };
            if (existing) await api.put(`/softwares/${existing.id}`, formData, cfg);
            else await api.post('/softwares', formData, cfg);
            setOpenSoftwareForm(false);
            setEditingSoftware(null);
            setSnackbar({ open: true, message: 'Salvo com sucesso!', severity: 'success' });
            fetchAllData(true);
        } catch (e) {
            setSnackbar({ open: true, message: 'Erro ao salvar', severity: 'error' });
            throw e;
        }
    };

    const handleConfirmDelete = async () => {
        const { type, item } = deleteDialog;
        try {
            if (type === 'category') {
                const newSections = dashboardConfig.sectionsConfig.filter(s => s.id !== item.id);
                setDashboardConfig(prev => ({ ...prev, sectionsConfig: newSections }));
                await api.put('/dashboard/config', { sectionsConfig: newSections });
                setSnackbar({ open: true, message: 'Categoria removida.', severity: 'warning' });
            } else if (type === 'software') {
                await api.delete(`/softwares/${item.id}`);
                setSnackbar({ open: true, message: 'Software removido.', severity: 'warning' });
                fetchAllData(true);
            }
        } catch (e) {
            setSnackbar({ open: true, message: 'Erro ao remover.', severity: 'error' });
        } finally {
            setDeleteDialog({ open: false, type: null, item: null });
        }
    };

    const sections = useMemo(() => (dashboardConfig.sectionsConfig || []).sort((a, b) => a.order - b.order), [dashboardConfig]);

    const isAllowedMap = useMemo(() => {
        const map = {};
        userAllowedIds.forEach(id => { map[id] = true; });
        return map;
    }, [userAllowedIds]);

    const allSystemsFiltered = useMemo(() => {
        let list = [...systems];
        if (searchTerm) list = list.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
        if (showOnlyAllowed) list = list.filter(s => userAllowedIds.includes(s.id));
        if (selectedStatus !== 'all') list = list.filter(s => s.status === selectedStatus);
        return list.sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [systems, searchTerm, showOnlyAllowed, selectedStatus, userAllowedIds]);

    const handleAccess = (s) => {
        if (!s.targetUrl) return alert("Sem URL");
        const sep = s.targetUrl.includes('?') ? '&' : '?';
        window.open(`${s.targetUrl}${sep}token=${localStorage.getItem('token')}`, '_self');
    };

    const categorySystems = useMemo(() => {
        if (!openCategoryModal) return [];
        let list = systems.filter(s => (s.section || 'Outros') === openCategoryModal.title);
        if (showOnlyAllowed) list = list.filter(s => userAllowedIds.includes(s.id));
        return list.sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [openCategoryModal, systems, showOnlyAllowed, userAllowedIds]);

    return (
        <ThemeProvider theme={customTheme}>
            <GlobalStyles styles={{
                'html, body, #root': {
                    backgroundColor: '#0f1218',
                    margin: 0, padding: 0,
                    minHeight: '100%', width: '100%',
                    overflowX: 'hidden',
                },
            }} />
            <CssBaseline />
            <Box sx={{
                width: '100%', minHeight: '100vh', bgcolor: 'background.default',
                pb: 8, overflowX: 'hidden', position: 'relative',
                '&::before': {
                    content: '""', position: 'fixed', inset: 0,
                    bgcolor: 'background.default', zIndex: -1,
                },
            }}>
                <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 }, pb: 4, px: { xs: 2, md: 3 }, bgcolor: 'transparent' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar
                                src={user?.avatarUrl ? getAssetUrl(user.avatarUrl) : undefined}
                                alt={user?.name || 'Usuário'}
                                sx={{
                                    width: 56, height: 56,
                                    bgcolor: 'primary.main', color: 'primary.contrastText',
                                    fontSize: '1.25rem', fontWeight: 700,
                                    border: '2px solid rgba(255,255,255,0.08)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                    flexShrink: 0,
                                }}>
                                {!user?.avatarUrl && user?.name
                                    ? user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                    : '?'}
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
                                    Olá, <span style={{ color: '#90caf9' }}>{user?.name?.split(' ')[0] || 'Usuário'}</span>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {userAllowedIds.length} sistemas liberados para você.
                                </Typography>
                            </Box>
                        </Stack>

                        {isSuperAdmin && (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <FormControlLabel
                                    control={<Switch checked={editMode} onChange={(e) => setEditMode(e.target.checked)} color="warning" />}
                                    label={<Typography variant="body2" fontWeight={600}>Modo edição</Typography>}
                                    sx={{ mr: 1 }} />
                                {editMode && (
                                    <Button variant="contained" size="small" startIcon={<Add />} onClick={handleOpenNewSoftware}>
                                        Novo card
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>

                    {(sections.length > 0 || (isSuperAdmin && editMode)) && (
                        <Box sx={{ mb: 5, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            {sections.length === 0 ? (
                                <>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                                        <Typography variant="h6" fontWeight={700}>Categorias</Typography>
                                        {isSuperAdmin && editMode && (
                                            <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleOpenNewCategory}
                                                sx={{ borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.15)' }}>
                                                Nova categoria
                                            </Button>
                                        )}
                                    </Stack>
                                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                        <Layers sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">Nenhuma categoria criada ainda.</Typography>
                                    </Paper>
                                </>
                            ) : (
                                <CategoryRow
                                    categories={sections}
                                    systems={systems}
                                    userAllowedIds={userAllowedIds}
                                    isSuperAdmin={isSuperAdmin}
                                    editMode={editMode}
                                    onSelectCategory={(cat) => setOpenCategoryModal(cat)}
                                    onEditCategory={handleOpenEditCategory}
                                    onDeleteCategory={(cat) => setDeleteDialog({ open: true, type: 'category', item: cat })}
                                    onNewCategory={handleOpenNewCategory}
                                />
                            )}
                        </Box>
                    )}

                    <Box>
                        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
                            <Typography variant="h6" fontWeight={700}>Todos os softwares</Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <TextField size="small" placeholder="Buscar..." value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                                        sx: { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1.5 },
                                    }}
                                    sx={{ minWidth: 200 }} />
                                <TextField select size="small" value={selectedStatus}
                                    onChange={e => setSelectedStatus(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    SelectProps={{ sx: { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1.5 } }}>
                                    <MenuItem value="all">Todos</MenuItem>
                                    {Object.entries(statusConfig).map(([key, config]) => (
                                        <MenuItem key={key} value={key}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: config.dot }} />
                                                <Typography variant="body2">{config.label}</Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button size="small" variant={showOnlyAllowed ? 'contained' : 'outlined'}
                                    onClick={() => setShowOnlyAllowed(!showOnlyAllowed)}
                                    startIcon={<GppGoodOutlined sx={{ fontSize: 16 }} />}
                                    sx={{ whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.1)' }}>
                                    {showOnlyAllowed ? 'Meus sistemas' : 'Mostrar todos'}
                                </Button>
                            </Stack>
                        </Stack>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                        ) : allSystemsFiltered.length === 0 ? (
                            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                <Search sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Nenhum software encontrado</Typography>
                                <Typography variant="body2" color="text.secondary">Tente ajustar os filtros de busca.</Typography>
                            </Paper>
                        ) : (
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    lg: 'repeat(4, minmax(0, 1fr))',
                                },
                                gap: 2.5, width: '100%',
                            }}>
                                {allSystemsFiltered.map(sys => (
                                    <SoftwareCard
                                        key={sys.id}
                                        system={sys}
                                        isAllowed={isAllowedMap[sys.id]}
                                        isSuperAdmin={isSuperAdmin}
                                        isEditMode={editMode}
                                        onAccess={handleAccess}
                                        onEdit={handleOpenEditSoftware}
                                        onDelete={(s) => setDeleteDialog({ open: true, type: 'software', item: s })}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </Container>

                <CategoryModal
                    open={!!openCategoryModal}
                    onClose={() => setOpenCategoryModal(null)}
                    category={openCategoryModal}
                    systems={categorySystems}
                    isAllowedMap={isAllowedMap}
                    isSuperAdmin={isSuperAdmin}
                    onAccess={handleAccess}
                />

                <CategoryFormModal
                    open={openCategoryForm}
                    onClose={() => { setOpenCategoryForm(false); setEditingCategory(null); }}
                    onSave={handleSaveCategory}
                    category={editingCategory}
                    existingTitles={(dashboardConfig.sectionsConfig || []).map(s => s.title)}
                />

                <SoftwareFormModal
                    open={openSoftwareForm}
                    onClose={() => { setOpenSoftwareForm(false); setEditingSoftware(null); }}
                    onSave={handleSaveSoftware}
                    system={editingSoftware}
                    categories={sections}
                />

                <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, item: null })} PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle fontWeight={700}>
                        {deleteDialog.type === 'category' ? 'Excluir categoria?' : 'Excluir software?'}
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Tem certeza que deseja excluir <strong>{deleteDialog.item?.title || deleteDialog.item?.name}</strong>?
                        </Typography>
                        {deleteDialog.type === 'category' && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                Os softwares desta categoria não serão excluídos.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>Cancelar</Button>
                        <Button color="error" variant="contained" onClick={handleConfirmDelete}>Excluir</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default DashboardPage;